import * as yup from "yup";
import i18next from "config/i18next";
import { AnyObject } from "yup/lib/types";
import {
  DateType,
  TimeType,
  ValidateHourParamsType,
  ValidateIntervalDifferenceParamsType,
  ValidateTimeIntervalParamsType,
} from "./types";
import moment from "moment";
import { DATE_FORMATS } from "./constants";
import { getLocalTimezoneOffset } from "./date";
import { AcountDataType } from "pages/pickers/types";

export const diferenceInMinutes = (initial: string, end: string) => {
  const [initialHour, initialMin] = initial.split(":");
  const [endHour, endMin] = end.split(":");

  const deltaHour = Number(endHour) - Number(initialHour);
  const deltaMin = Number(endMin) - Number(initialMin);
  const deltaMinTotal = deltaMin + deltaHour * 60;
  return deltaMinTotal;
};

export const validateHourDifference = (
  context: yup.TestContext<AnyObject>,
  params: ValidateHourParamsType
) => {
  const { createError } = context;
  const time = context.parent as TimeType;

  if (!time.initial || !time.end) return true;

  const deltaMinTotal = diferenceInMinutes(time.initial, time.end);
  const message =
    deltaMinTotal < 0
      ? i18next.t("validations:initialGreaterThanEnd")
      : params.minDifference && deltaMinTotal < params.minDifference
      ? params.minMessage
      : params.maxDifference && deltaMinTotal > params.maxDifference
      ? params.maxMessage
      : "";

  if (message) {
    return createError({
      message,
      path: params.field,
    });
  }

  return true;
};

export const validateIntervalDifference = (
  context: yup.TestContext<AnyObject>,
  params: ValidateIntervalDifferenceParamsType
) => {
  const { createError } = context;
  const time = context.parent as TimeType;

  if (!time.initial || !time.end) return true;

  const [initialHour, initialMin] = time.initial.split(":");
  const [endHour, endMin] = time.end?.split(":");

  const deltaHour = Number(endHour) - Number(initialHour);
  const deltaMin = Number(endMin) - Number(initialMin);
  const deltaMinTotal = deltaMin + deltaHour * 60;
  const isValid = deltaMinTotal % params.interval === 0;

  const message =
    deltaMinTotal < 0
      ? i18next.t("validations:initialGreaterThanEnd")
      : !isValid
      ? params.message
      : "";

  if (message) {
    return createError({
      message,
      path: params.field,
    });
  }

  return true;
};

export const validateDateRange = (
  value: DateType,
  context: yup.TestContext<AnyObject>,
  maxRange: number
) => {
  const { createError } = context;
  const field = "date";

  if (!(value && value.from))
    return createError({
      message: i18next.t("validations:required"),
      path: field,
    });

  if (value.from && value.until) {
    const initialDate = moment(value.from, DATE_FORMATS.extraShortDate);
    const finalDate = moment(value.until, DATE_FORMATS.extraShortDate);
    const dateDiff = finalDate.diff(initialDate, "days");
    if (dateDiff >= maxRange) {
      return createError({
        message: i18next.t("validations:maxDate", {
          data: maxRange,
        }),
        path: field,
      });
    }
  }
  return true;
};

export const validateTimeInterval = (
  value: string,
  context: yup.TestContext<AnyObject>,
  params: ValidateTimeIntervalParamsType
) => {
  const { field, interval, message } = params;
  const { createError } = context;

  const [, minutes] = value.split(":");
  const m = Number(minutes);
  if (!minutes || isNaN(m)) return true;

  if (m % interval !== 0) {
    return createError({
      message,
      path: field,
    });
  }

  return true;
};

export const validateIsAfterNow = (
  value: string | undefined,
  context: yup.TestContext<AnyObject>,
  field: string,
  dateField: string = "date"
) => {
  const { parent, createError } = context;
  const date = parent[dateField];
  if (!value || !date) return true;

  const offset = getLocalTimezoneOffset() || 0;
  const now = moment().add(offset, "hours");
  const [hours, minutes] = value.split(":");
  const startTime = moment(date, DATE_FORMATS.shortDate)
    .add(Number(hours), "hours")
    .add(Number(minutes), "minutes");
  const isError = startTime.isBefore(now, "minutes");
  if (isError) {
    return createError({
      message: i18next.t("validations:afterNow"),
      path: field,
    });
  }

  return true;
};

export const validateFiscalNumber = (
  value: string | undefined | null,
  context: yup.TestContext<AnyObject>
) => {
  const account = context.parent as AcountDataType;
  const { createError } = context;

  if (!account.sapInterlocutor || account.sapInterlocutor === "-") return true;

  if (!value || value === "-") {
    return createError({
      message: i18next.t("validations:required"),
    });
  }
  return true;
};

export const validateIsAfterPreviosValue = (
  value: string,
  context: yup.TestContext<AnyObject>,
  params: ValidateTimeIntervalParamsType
) => {
  const { field, interval, message } = params;
  const { createError } = context;

  const [, minutes] = value.split(":");
  const m = Number(minutes);
  if (!minutes || isNaN(m)) return true;

  if (m % interval !== 0) {
    return createError({
      message,
      path: field,
    });
  }

  return true;
};
